/**
 * 인증 관련 유틸리티 함수들
 */

/**
 * JWT 토큰이 만료되었는지 확인
 * @param {string} token - JWT 토큰
 * @returns {boolean} - 토큰이 만료되었으면 true, 아니면 false
 */
export const isTokenExpired = (token) => {
  if (!token) return true;

  try {
    // 한글 깨짐 방지를 위한 디코딩 로직 개선
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join(''),
    );

    const payload = JSON.parse(jsonPayload);
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch (error) {
    console.error('토큰 파싱 오류:', error);
    return true;
  }
};

/**
 * 현재 저장된 토큰이 만료되었는지 확인
 * @returns {boolean} - 토큰이 만료되었으면 true, 아니면 false
 */
export const isCurrentTokenExpired = () => {
  const token = localStorage.getItem('POSTPOP_AUTH_TOKEN');
  return isTokenExpired(token);
};

/**
 * 현재 페이지 URL을 localStorage에 저장하고 로그인 페이지로 리다이렉트
 * @param {Function} navigate - React Router의 navigate 함수
 *
 * 사용 시점: React 컴포넌트 내부에서 사용자 인증 체크 시
 * 특징: React Router의 navigate 사용으로 SPA 네비게이션
 */
export const saveCurrentPathAndRedirectToLogin = (navigate) => {
  const currentPath = window.location.pathname + window.location.search;
  navigate(`/login?redirectUrl=${encodeURIComponent(currentPath)}`);
};

/**
 * 현재 페이지 URL을 localStorage에 저장하고 로그인 페이지로 리다이렉트 (스크롤 메모리 포함)
 * @param {Function} navigateWithScrollMemory - 스크롤 메모리가 포함된 navigate 함수
 *
 * 사용 시점: React 컴포넌트 내부에서 스크롤 위치 보존이 필요한 경우
 * 특징: 스크롤 메모리 보존 + React Router의 navigate 사용
 */
export const saveCurrentPathAndRedirectToLoginWithScroll = (navigateWithScrollMemory) => {
  const currentPath = window.location.pathname + window.location.search;
  navigateWithScrollMemory(`/login?redirectUrl=${encodeURIComponent(currentPath)}`);
};

/**
 * 현재 페이지 URL을 localStorage에 저장하고 로그인 페이지로 리다이렉트 (window.location.href 사용)
 *
 * 사용 시점: React 컴포넌트 외부 (axios 인터셉터 등)에서 실행되는 경우
 * 특징: 브라우저 기본 네비게이션 사용으로 React 컨텍스트 외부에서도 동작
 *
 * 왜 window.location.href를 사용하는가?
 * - axios 인터셉터는 React 컴포넌트 외부에서 실행됨
 * - React Router의 navigate 함수에 접근할 수 없음
 * - 브라우저 기본 네비게이션으로 페이지 전체 새로고침
 */
export const saveCurrentPathAndRedirectToLoginWithHref = () => {
  // 이미 로그인 페이지에 있는 경우 중복 리다이렉트 방지
  if (window.location.pathname === '/login') {
    return;
  }

  const currentPath = window.location.pathname + window.location.search;
  window.location.replace(`/login?redirectUrl=${encodeURIComponent(currentPath)}`);
};
